import { nextTick, onMounted, onUnmounted, watch, type Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getWordList } from "@/common/graphql/expression";
import { getChainInfo } from "@/common/graphql/chain";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetPrefByChainDocument,
  GetPrefByChainQuery,
  ListDeliveryAreaDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
} from "@/generated/graphql";
import { DELIVERY_REQUEST_REGISTER_START_FILE_NAME } from "../constants";

/**
 * 配達依頼登録開始画面の初期化処理に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterStartInitParams {
  wordList: Ref<Record<string, string>>;
  house_card_display: Ref<number>;
  is_hide_other_shop_members: Ref<number>;
  use_receipt_barcode_search_flg: Ref<number>;
  isReceiptBarcodeFocusing: Ref<boolean>;
  prefListItems: Ref<(GetPrefByChainQuery["getPrefByChain"][number] | null)[]>;
  areaListItems: Ref<(ListDeliveryAreaQuery["listDeliveryArea"][number] | null)[]>;
  receipt_barcode_input: Ref<{ focus?: () => void } | null>;
  receipt_barcode: Ref<string>;
  dialog: Ref<boolean>;
  receipt_barcode_house_card: Ref<string>;
  register_no: Ref<number>;
  receipt_no: Ref<number>;
  payment_amount: Ref<number>;
  showDeliveryAvailability: (areaCode: string | null) => Promise<void>;
  getDeliveryAvailable: () => Promise<void>;
  onClickDeliveryRequest: () => Promise<void>;
}

interface UseDeliveryRequestRegisterStartInitReturn {
  onReceiptBarcodeUpdate: (value: string | number | null | undefined) => void;
  onReceiptBarcodeKeyDown: (event: KeyboardEvent) => void;
}

/**
 * 配達依頼登録開始画面の初期化処理を扱う composable。
 */
export const useDeliveryRequestRegisterStartInit = ({
  wordList,
  house_card_display,
  is_hide_other_shop_members,
  use_receipt_barcode_search_flg,
  isReceiptBarcodeFocusing,
  prefListItems,
  areaListItems,
  receipt_barcode_input,
  receipt_barcode,
  dialog,
  receipt_barcode_house_card,
  register_no,
  receipt_no,
  payment_amount,
  showDeliveryAvailability,
  getDeliveryAvailable,
  onClickDeliveryRequest,
}: UseDeliveryRequestRegisterStartInitParams): UseDeliveryRequestRegisterStartInitReturn => {
  // 配達便の定期再取得タイマー
  let interval: ReturnType<typeof setInterval> | null = null;

  // レシート検索で取得した一時的な会員識別情報をクリアする
  const clearReceiptBarcodeSearchState = (): void => {
    receipt_barcode_house_card.value = "";
    register_no.value = 0;
    receipt_no.value = 0;
    payment_amount.value = 0;
  };

  // 初期表示時に必要なマスタ・設定・配達便を取得する
  onMounted(async () => {
    // レシートバーコードフォーカス完了まで他入力を抑止する
    isReceiptBarcodeFocusing.value = true;

    const apolloClient = await generateApolloClient();

    const wordData = await getWordList(
      localStorageGetChainCode(),
      DELIVERY_REQUEST_REGISTER_START_FILE_NAME,
      localStorageGetCustomerCode()
    );
    wordList.value = wordData;

    const chainInfo = await getChainInfo(
      localStorageGetCustomerCode(),
      localStorageGetChainCode()
    );
    if (chainInfo) {
      // チェーン設定から画面表示フラグを反映
      house_card_display.value = chainInfo.house_card_display
        ? chainInfo.house_card_display
        : 0;
      is_hide_other_shop_members.value =
        chainInfo.is_hide_other_shop_members ?? 0;
      use_receipt_barcode_search_flg.value =
        chainInfo.use_receipt_barcode_search_flg
          ? chainInfo.use_receipt_barcode_search_flg
          : 0;
    }

    const prefList: ApolloQueryResult<GetPrefByChainQuery> =
      await apolloClient.query<GetPrefByChainQuery>({
        query: GetPrefByChainDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
        fetchPolicy: "no-cache",
      });
    prefListItems.value = prefList.data.getPrefByChain ?? [];

    // エリア未指定の配達便一覧を初期表示
    await showDeliveryAvailability(null);

    const listArea: ApolloQueryResult<ListDeliveryAreaQuery> =
      await apolloClient.query<
        ListDeliveryAreaQuery,
        ListDeliveryAreaQueryVariables
      >({
        query: ListDeliveryAreaDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode(),
          shop_code: localStorageGetShopCode(),
        },
      });
    areaListItems.value = listArea.data.listDeliveryArea ?? [];

    // 入力画面への受け渡し用データを初期取得し、以降30秒ごとに更新
    await getDeliveryAvailable();
    interval = setInterval(() => {
      getDeliveryAvailable();
    }, 30000);

    // レシートバーコード検索有効時は入力欄へフォーカス
    if (use_receipt_barcode_search_flg.value) {
      await nextTick();
      receipt_barcode_input.value?.focus?.();
    }

    // フォーカス完了（またはレシートバーコード無効）で入力抑止を解除
    isReceiptBarcodeFocusing.value = false;
  });

  onUnmounted(() => {
    // 画面離脱時にポーリング停止
    if (interval !== null) {
      clearInterval(interval);
    }
  });

  // 全角数字を半角へ変換し、数字以外を除去して最大16桁に揃える
  const normalizeReceiptBarcode = (receiptBarcode: string): string => {
    let normalized = "";

    for (const char of receiptBarcode) {
      const code = char.charCodeAt(0);

      if (code >= 0xff10 && code <= 0xff19) {
        normalized += String.fromCharCode(code - 0xfee0);
        continue;
      }

      if (code >= 0x30 && code <= 0x39) {
        normalized += char;
      }
    }

    return normalized.slice(0, 16);
  };

  // バーコードリーダーやIME入力を受けて、正規化した値を反映する
  const onReceiptBarcodeUpdate = (
    value: string | number | null | undefined
  ): void => {
    receipt_barcode.value = normalizeReceiptBarcode(String(value ?? ""));
  };

  // 直接のキー入力では半角数字以外を受け付けない
  const onReceiptBarcodeKeyDown = (event: KeyboardEvent): void => {
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      /^[0-9]$/.test(event.key)
    ) {
      return;
    }

    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    event.preventDefault();
  };

  watch(receipt_barcode, (newValue) => {
    // 16桁入力完了時に自動検索
    const normalizedReceiptBarcode = normalizeReceiptBarcode(newValue ?? "");

    if (normalizedReceiptBarcode !== newValue) {
      receipt_barcode.value = normalizedReceiptBarcode;
      return;
    }

    if (normalizedReceiptBarcode.length !== 16) {
      clearReceiptBarcodeSearchState();
    }

    if (normalizedReceiptBarcode.length === 16) {
      void onClickDeliveryRequest();
    }
  });

  watch(dialog, (newValue) => {
    // 会員候補ダイアログを閉じたら一時保持値をクリア
    if (!newValue) {
      clearReceiptBarcodeSearchState();
    }
  });

  return {
    onReceiptBarcodeUpdate,
    onReceiptBarcodeKeyDown,
  };
};
